import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getPurchaseListApi } from '~/features/purchase/api/get-purchase-list.api';
import { getPurchaseStatisticsApi } from '~/features/purchase/api/get-purchase-statistics.api';
import { getPurchaseApi } from '~/features/purchase/api/get-purchase.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  Purchase,
  PurchaseListParams,
  PurchaseStatistics,
  PurchaseStatisticsParams,
} from '~/features/purchase/types/purchase.type';

export interface PurchaseState {
  isLoading: boolean;
  list: Purchase[];
  meta: PaginationMeta | null;
  params: PurchaseListParams | null;
  selectedPurchase: Purchase | null;
  statistics: PurchaseStatistics | null;
}

const initialState: PurchaseState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedPurchase: null,
  statistics: null,
};

/**
 * 구매 목록 조회
 */
export const fetchPurchaseList = createAsyncThunk<
  PaginatedApiResponse<Purchase>,
  PurchaseListParams | undefined
>('purchase/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getPurchaseListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 구매 상세 조회
 */
export const fetchPurchaseDetail = createAsyncThunk<Purchase, number>(
  'purchase/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getPurchaseApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 구매 통계 조회
 */
export const fetchPurchaseStatistics = createAsyncThunk<
  PurchaseStatistics,
  PurchaseStatisticsParams | undefined
>('purchase/fetchStatistics', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getPurchaseStatisticsApi(params);
    return data.data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

const PurchaseSlice = createSlice({
  initialState,
  name: 'purchase',
  reducers: {
    setSelectedPurchase: (state, action) => {
      state.selectedPurchase = action.payload;
    },
    resetSelectedPurchase: state => {
      state.selectedPurchase = null;
    },
    resetPurchase: state => {
      state.selectedPurchase = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
      state.statistics = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchPurchaseList
      .addCase(fetchPurchaseList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchPurchaseList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchPurchaseList.rejected, state => {
        state.isLoading = false;
      })
      // fetchPurchaseDetail
      .addCase(fetchPurchaseDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchPurchaseDetail.fulfilled, (state, action) => {
        state.selectedPurchase = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchPurchaseDetail.rejected, state => {
        state.isLoading = false;
      })
      // fetchPurchaseStatistics
      .addCase(fetchPurchaseStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      });
  },
});

export const { setSelectedPurchase, resetSelectedPurchase, resetPurchase } = PurchaseSlice.actions;
export default PurchaseSlice;
